import React, { useEffect, useState } from "react";
import { Calendar } from "./ui/calendar";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";

/**
 * SlotPicker — Shadcn calendar + available time slots from /api/availability
 * Props:
 *  - serviceId: string (required)
 *  - value: { date: 'YYYY-MM-DD'|'', time: 'HH:MM'|'' }
 *  - onChange: (value) => void
 */
export default function SlotPicker({ serviceId, value, onChange }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState(null);

  const selectedDate = value?.date ? new Date(value.date + "T00:00:00") : undefined;

  useEffect(() => {
    if (!serviceId || !value?.date) { setSlots([]); return; }
    setLoading(true);
    api
      .get(`/availability?service_id=${serviceId}&date=${value.date}`)
      .then((r) => { setSlots(r.data.slots || []); setReason(r.data.reason || null); })
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [serviceId, value?.date]);

  const onSelectDate = (d) => {
    if (!d) return;
    const iso = d.toISOString().slice(0, 10);
    onChange({ date: iso, time: "" });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="slot-picker">
      <div>
        <label className="overline block mb-3">Select Date *</label>
        <div className="border border-white/10 p-2 bg-[#0A0A0A]">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={onSelectDate}
            disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
            initialFocus
            data-testid="slot-picker-calendar"
          />
        </div>
      </div>
      <div>
        <label className="overline block mb-3">Available Times *</label>
        {!value?.date && <p className="text-white/50 text-sm">Pick a date to see available time slots.</p>}
        {value?.date && loading && (
          <div className="flex items-center gap-2 text-white/60 text-sm"><Loader2 className="animate-spin" size={14}/> Loading slots…</div>
        )}
        {value?.date && !loading && slots.length === 0 && (
          <p className="text-white/60 text-sm">
            {reason === "blackout" ? "This date is unavailable." :
             reason === "closed" ? "No hours scheduled on this day." :
             reason === "past_date" ? "Please pick a future date." :
             "No open slots — please choose another day."}
          </p>
        )}
        {slots.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slot-picker-slots">
            {slots.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => onChange({ ...value, time: t })}
                className={`py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  value.time === t
                    ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                    : "border-white/15 text-white/80 hover:border-[#D4AF37]/60"
                }`}
                data-testid={`slot-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
